import type { ProviderListingReference } from "@/features/providers";
import type { AdoptionStatus } from "@/features/listings";
import type { CollectionWagsListing } from "@/features/listings";

export type ProviderDogListingCard = {
    id: string;
    name: string;
    imageUrl: string;
    breedNames: string[];
    isNew?: boolean;

    status?: AdoptionStatus;
    priceRange: CollectionWagsListing["priceRange"];
};

export function providerSummaryToCard(
    listing: Pick<
        CollectionWagsListing,
        "id" | "name" | "imageUrls" | "breeds" | "status" | "priceRange"
    >,
    opts: { fallbackName: string; fallbackImageUrl?: string },
): ProviderDogListingCard {
    const breedNames = listing.breeds
        .map((breed) => breed.name)
        .filter((name): name is string => Boolean(name));
    return {
        id: listing.id,
        name: listing.name.trim() || opts.fallbackName,
        imageUrl: listing.imageUrls[0] ?? opts.fallbackImageUrl ?? "",
        breedNames: breedNames.length > 0 ? breedNames : ["Unknown breed"],
        status: listing.status,
        priceRange: listing.priceRange,
    };
}

export function providerReferenceToCard(
    reference: ProviderListingReference,
    opts: { fallbackName: string; fallbackImageUrl?: string },
): ProviderDogListingCard {
    return {
        id: reference.listingId,
        name: reference.name.trim() || opts.fallbackName,
        imageUrl: reference.primaryImageUrl ?? opts.fallbackImageUrl ?? "",
        breedNames:
            reference.breedNames.length > 0
                ? reference.breedNames
                : ["Unknown breed"],
        status: reference.status,
        priceRange: null,
    };
}

export function providerCardsFromReferences(
    references: ProviderListingReference[],
    hydratedById: Map<string, CollectionWagsListing>,
    fallbackName: string,
    fallbackImageUrl?: string,
): ProviderDogListingCard[] {
    return references.map((reference, index) => {
        const hydrated = hydratedById.get(reference.listingId);
        const name = `${fallbackName} ${index + 1}`;
        if (!hydrated) {
            return providerReferenceToCard(reference, {
                fallbackName: name,
                fallbackImageUrl,
            });
        }
        return providerSummaryToCard(hydrated, {
            fallbackName: reference.name.trim() || name,
            fallbackImageUrl: reference.primaryImageUrl ?? fallbackImageUrl,
        });
    });
}
